import { useState, useEffect, useRef } from 'react';
import { useVoltarNavegador } from './useVoltarNavegador';

/**
 * Mantém um elemento montado um pouco mais depois que "aberto" vira
 * falso, só pra dar tempo da animação de saída (CSS) tocar antes de
 * desmontar de vez — sem isso, o React tira o elemento da tela na
 * hora, e a transição de fechamento nunca chega a aparecer (11/07/2026).
 *
 * Uso:
 *   const { montado, fechando } = useSaidaSuave(aberto, 200);
 *   if (!montado) return null;
 *   <div className={`modal-overlay ${fechando ? 'closing' : ''}`}>...
 *
 * duracaoMs deve bater com a duração da animação de saída no CSS
 * (.closing), senão ou corta cedo ou demora mais que precisa.
 *
 * Terceiro parâmetro OPCIONAL, aoFecharPeloBotaoFisico (03/08/2026,
 * achado real: um formulário/modal aberto sem isso faz o botão físico
 * de voltar do celular — ou o gesto de swipe back — pular direto pra
 * ROTA anterior do navegador, tipo a sala de onde a pessoa veio, em
 * vez de só fechar o modal). Quando fornecido, deve ser a MESMA função
 * que já fecha o modal (ex: `() => setModal(false)`) — o hook cuida
 * de empilhar/escutar o histórico por baixo, reaproveitando
 * useVoltarNavegador. Sem esse parâmetro, o hook se comporta
 * exatamente como antes (retrocompatível com as chamadas existentes).
 */
export function useSaidaSuave(aberto, duracaoMs = 200, aoFecharPeloBotaoFisico) {
  const [montado, setMontado] = useState(aberto);
  const [fechando, setFechando] = useState(false);
  const timeoutRef = useRef(null);

  /* ⚠⚠ `ultimo` — O VALOR SOBREVIVE À ANIMAÇÃO DE SAÍDA (12/09/2026,
     tela branca achada pelo dono do produto na Lixeira: abrir um item e
     clicar no X dava `Cannot read properties of null`).

     A armadilha é da própria ideia do hook. Quem passa um BOOLEANO não
     tem problema. Quem passa um OBJETO — `useSaidaSuave(!!previa)` — está
     usando a mesma variável para duas coisas: "está aberto" e "o que
     mostrar". Ao fechar, o objeto vira nulo na hora, mas `montado`
     continua verdadeiro por mais 200ms para a animação tocar — e o corpo
     do modal lê `previa.carregando` de um nulo.

     ⚠ NÃO É DEFEITO DE UMA TELA: **61 arquivos passam objeto para este
     hook.** Por isso o conserto é aqui e não lá.

     `ultimo` guarda o último valor não-nulo e continua entregando ele
     durante a saída. Para quem passa booleano, `ultimo` é `true` e
     ninguém precisa mudar nada. */
  const ultimoRef = useRef(aberto);
  if (aberto) ultimoRef.current = aberto;

  useEffect(() => {
    if (aberto) {
      clearTimeout(timeoutRef.current);
      setMontado(true);
      setFechando(false);
    } else if (montado) {
      setFechando(true);
      timeoutRef.current = setTimeout(() => {
        setMontado(false);
        setFechando(false);
      }, duracaoMs);
    }
    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  useVoltarNavegador(!!aoFecharPeloBotaoFisico && aberto, aoFecharPeloBotaoFisico || (() => {}));

  return { montado, fechando, ultimo: ultimoRef.current };
}
